import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Label, HelperText, ErrorText, Heading, Text } from '../ui/Text';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

const createRegisterSchema = (requireInviteCode: boolean) =>
  z
    .object({
      name: z.string().min(1, 'Name is required').max(255, 'Name too long').trim().optional(),
      email: z.string().min(1, 'Email is required').email('Invalid email format'),
      username: z
        .string()
        .min(3, 'Username must be at least 3 characters')
        .max(25, 'Username must be less than 25 characters')
        .regex(
          /^[a-zA-Z0-9_-]+$/,
          'Username can only contain letters, numbers, hyphens, and underscores'
        )
        .toLowerCase()
        .trim(),
      password: z
        .string()
        .min(12, 'Password must be at least 12 characters')
        .max(128, 'Password must be less than 128 characters'),
      confirmPassword: z.string(),
      inviteCode: requireInviteCode
        ? z
            .string()
            .min(1, 'Invite code is required')
            .regex(
              /^[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}$/,
              'Invalid invite code format (XXX-XXX-XXX)'
            )
        : z.string().optional(),
    })
    .refine(data => data.password === data.confirmPassword, {
      message: "Passwords don't match",
      path: ['confirmPassword'],
    });

type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

export function RegisterForm() {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [requireInviteCode] = useState<boolean>(true); // Force to true since backend requires invite codes
  const [isCheckingInviteRequirement] = useState<boolean>(false);
  const [passwordValue, setPasswordValue] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    reset,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(createRegisterSchema(requireInviteCode)),
  });

  // Check if invite codes are required
  // TODO: Implement proper API endpoint to check this setting
  // useEffect(() => {
  //   const checkInviteRequirement = async () => {
  //     try {
  //       // Try to register with minimal data to see if invite code is required
  //       await api.post('/auth/register', {
  //         email: 'test@example.com',
  //         password: 'testpassword123',
  //         name: 'Test User'
  //       });
  //       setRequireInviteCode(false);
  //     } catch (error: unknown) {
  //       const errorMessage = error && typeof error === 'object' && 'response' in error
  //         ? ((error as { response?: { data?: { error?: string } } }).response?.data?.error || '')
  //         : '';
  //
  //       if (errorMessage.toLowerCase().includes('invite')) {
  //         setRequireInviteCode(true);
  //       } else {
  //         setRequireInviteCode(false);
  //       }
  //     } finally {
  //       setIsCheckingInviteRequirement(false);
  //     }
  //   };
  //   checkInviteRequirement();
  // }, []);

  // Pre-fill invite code from URL params
  useEffect(() => {
    const inviteCode = searchParams.get('inviteCode');
    if (inviteCode) {
      setValue('inviteCode', inviteCode);
    }
  }, [searchParams, setValue]);

  // Update form resolver when invite requirement changes
  useEffect(() => {
    // This will trigger validation with the new schema
    reset();
  }, [requireInviteCode, reset]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError(null);
      await registerUser({
        ...(data.name && { name: data.name }),
        email: data.email,
        username: data.username,
        password: data.password,
        ...(data.inviteCode && { inviteCode: data.inviteCode }),
      });
      // Navigate to intended destination or dashboard
      const from =
        (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card shadow-card-hover rounded-lg px-8 py-10 border border-border">
        <div className="text-center mb-8">
          <div className="text-4xl mb-4">🌱</div>
          <Heading as="h1" size="md">
            Create account
          </Heading>
          <Text variant="body" className="mt-2">
            Start tracking your plants today
          </Text>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <ErrorText>{error}</ErrorText>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {requireInviteCode && (
            <div>
              <Label htmlFor="inviteCode">Invite code</Label>
              <Input
                id="inviteCode"
                type="text"
                placeholder="XXX-XXX-XXX"
                error={errors.inviteCode?.message}
                {...register('inviteCode')}
              />
              <HelperText>Enter your invite code in the format XXX-XXX-XXX</HelperText>
            </div>
          )}

          <div>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              placeholder="Choose a username"
              error={errors.username?.message}
              {...register('username')}
            />
            <HelperText>
              3-25 characters, letters, numbers, hyphens, and underscores only
            </HelperText>
          </div>

          <div>
            <Label htmlFor="name">Full name (optional)</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              error={errors.name?.message}
              {...register('name')}
            />
          </div>

          <div>
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              error={errors.email?.message}
              {...register('email')}
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Create a password"
              error={errors.password?.message}
              {...register('password', {
                onChange: e => setPasswordValue(e.target.value),
              })}
            />
            {passwordValue && (
              <div className="mt-3">
                <PasswordStrengthMeter password={passwordValue} />
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            size="lg"
            isLoading={isSubmitting || isCheckingInviteRequirement}
            disabled={isCheckingInviteRequirement}
          >
            {isCheckingInviteRequirement ? 'Loading...' : 'Create account'}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-green-600 hover:text-green-500">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
